import { authApi } from './api';

// ── Safari-safe localStorage helpers ────────────────────────────────────────
// Safari Private Mode throws SecurityError on localStorage access.
function lsGet(key)      { try { return localStorage.getItem(key); }            catch { return null; } }
function lsSet(key, val) { try { localStorage.setItem(key, val); }              catch {} }
function lsRemove(key)   { try { localStorage.removeItem(key); }                catch {} }

export const login = async (email, password) => {
  const res = await authApi?.login({ email, password });
  const payload = res?.data?.data || {};
  // 2FA challenge — return the temp token for the second step
  if (payload?.requires2FA) {
    return { requires2FA: true, temp_token: payload.temp_token };
  }
  const { token, refresh_token, user } = payload;
  if (token) {
    lsSet('auth_token', token);
    lsSet('auth_refresh_token', refresh_token || '');
    lsSet('auth_user', JSON.stringify(user));
  }
  return { token, user };
};

export const register = async (data) => {
  const res = await authApi?.register(data);
  const payload = res?.data?.data || {};
  // New flow: server returns { requiresVerification: true, email } — no token issued
  if (payload?.requiresVerification) {
    return { requiresVerification: true, email: payload.email };
  }
  // Legacy / admin bypass: server returned a token
  const { token, refresh_token, user } = payload;
  if (token) {
    lsSet('auth_token', token);
    lsSet('auth_refresh_token', refresh_token || '');
    lsSet('auth_user', JSON.stringify(user));
  }
  return { token, user };
};

export const logout = async () => {
  try {
    await authApi?.logout();
  } catch (_) {
    // Ignore errors on logout
  } finally {
    lsRemove('auth_token');
    lsRemove('auth_refresh_token');
    lsRemove('auth_user');
  }
};

export const forgotPassword = async (email) => {
  const res = await authApi?.forgotPassword({ email });
  return res?.data;
};

export const resetPassword = async (token, password) => {
  const res = await authApi?.resetPassword({ token, password });
  return res?.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const res = await authApi?.changePassword({
    current_password: currentPassword,
    new_password: newPassword,
  });
  return res?.data;
};

export const getMe = async () => {
  const res = await authApi?.getMe();
  return res?.data?.data;
};

export const updateProfile = async (data) => {
  const res = await authApi?.updateProfile(data);
  const user = res?.data?.data;
  if (user) {
    lsSet('auth_user', JSON.stringify(user));
  }
  return user;
};

export const refreshToken = async () => {
  const refreshTok = lsGet('auth_refresh_token');
  if (!refreshTok) return null;
  const res = await authApi?.refresh({ refresh_token: refreshTok });
  const { token } = res?.data?.data || {};
  if (token) {
    lsSet('auth_token', token);
  }
  return token;
};

export const getStoredUser = () => {
  try {
    const raw = lsGet('auth_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getStoredToken = () => lsGet('auth_token');

export const isAuthenticated = () => !!getStoredToken();
